import React, { useEffect, useState } from "react";
import { Link as LinkIcon, Send, Image as ImageIcon, Clock, Eye } from "lucide-react";
import { useAuth } from "../../services/AuthContext.js";
import { Link, useNavigate } from "react-router-dom";
import { AccountSpecificMessagePublic } from "../../components/EnrollButton.js";
import { getPostsByAuthor, deletePost } from "../../services/user_posts.js";
import { createChat } from "../../services/chats.js";
import CreatePost from "./create_post.js";
import EditPostModal from "./edit_post.js";
import OwnerActionsMenu from "../../components/OwnerActionsMenu.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import "./account.css";
import "./account_public.css";

const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;
const default_profile_image_url =
  "https://dominio-eletrico-profile-images-dev.s3.us-east-1.amazonaws.com/default_user_image.jpg";

const formatDateTime = (iso) => {
  try {
    return new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium" }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const PostPreviewCard = ({
  post,
  isOwner,
  onEdit,
  onRequestDelete,
  actionsDisabled,
}) => {
  const images = post.image_urls || [];
  const [imgSrc, setImgSrc] = useState(images[0]);
  const viewsCount = Number(post.views_count) || 0;

  useEffect(() => {
    setImgSrc(images[0]);
  }, [images[0]]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="profile-post-card-wrap">
      {isOwner && (
        <div className="profile-post-actions">
          <OwnerActionsMenu
            disabled={actionsDisabled}
            onEdit={() => onEdit?.(post)}
            onDelete={() => onRequestDelete?.(post)}
          />
        </div>
      )}
      <Link to={`/post/${post.id}`} className="profile-post-card">
        <div className="profile-post-thumb">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt="Imagem da publicação"
              className="profile-post-thumb-img"
              onError={() => setImgSrc(null)}
            />
          ) : (
            <div className="profile-post-thumb-fallback">
              <ImageIcon size={28} />
            </div>
          )}
          {images.length > 1 && (
            <span className="profile-post-thumb-count">
              <ImageIcon size={12} /> {images.length}
            </span>
          )}
        </div>

        <div className="profile-post-body">
          <h3 className="profile-post-title">{post.title}</h3>
          <p className="profile-post-desc">{post.description}</p>
          <div className="profile-post-meta">
            <span>
              <Clock size={14} /> {formatDateTime(post.created_at)}
            </span>
            <span>
              <Eye size={14} />{" "}
              {viewsCount === 1 ? "1 visualização" : `${viewsCount} visualizações`}
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

function AccountPublic({ slug }) {
  const { user, getIdToken } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postPendingDelete, setPostPendingDelete] = useState(null);
  const [deletingPost, setDeletingPost] = useState(false);
  const navigate = useNavigate();

  const authorId = account?.user_id;

  useEffect(() => {
    if (!authorId) return;

    let isMounted = true;

    const fetchPosts = async () => {
      try {
        setPostsLoading(true);
        const token = await getIdToken();
        const data = await getPostsByAuthor(token, authorId);
        if (isMounted) setPosts(data);
      } catch (err) {
        console.error("Erro ao carregar publicações:", err);
        if (isMounted) setPosts([]);
      } finally {
        if (isMounted) setPostsLoading(false);
      }
    };

    fetchPosts();

    return () => {
      isMounted = false;
    };
  }, [authorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreatePost = (post) => {
    setPosts((prev) => [post, ...prev]);
  };

  const handlePostUpdated = (updated) => {
    setPosts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    setEditingPost(null);
  };

  const handleConfirmDeletePost = async () => {
    if (!postPendingDelete || deletingPost) return;
    const postId = postPendingDelete.id;
    try {
      setDeletingPost(true);
      const token = await getIdToken();
      await deletePost(token, postId);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      setPostPendingDelete(null);
    } catch (err) {
      console.error("Erro ao excluir publicação:", err);
      if (err.status === 403) {
        setPostPendingDelete(null);
      } else if (err.status === 404) {
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        setPostPendingDelete(null);
      } else {
        setError(err.message || "Não foi possível excluir a publicação.");
        setPostPendingDelete(null);
      }
    } finally {
      setDeletingPost(false);
    }
  };

  const initiateDirectMessage = async (receiverId) => {
    try {
      if (receiverId === user.cognitoUser.username) {
        setError("Você não pode enviar uma mensagem direta para si mesmo.");
        return;
      }
      const token = await getIdToken();
      const chat = await createChat(token, {
        type: "direct",
        other_user_id: receiverId,
      });
      navigate(`/chat/${chat.code}`);
    } catch (err) {
      console.error("Error creating direct message:", err);
      setError(err.message || "Não foi possível iniciar a mensagem direta");
    }
  };

  useEffect(() => {
    if (!slug) return;

    let isMounted = true;

    const fetchAccount = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getIdToken();
        const response = await fetch(`${baseAPI_URL}/users/get-user-data-by-slug/${slug}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();

        if (isMounted) {
          if (data?.error) {
            setError(data.error);
          } else {
            setAccount(data);
          }
        }
      } catch (err) {
        console.error("Erro ao carregar conta:", err);
        if (isMounted) setError("Não foi possível carregar as informações da conta");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAccount();

    return () => {
      isMounted = false;
    };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setImageError(false);
  }, [account]);

  if (loading) return <div className="loading-message">Carregando informações...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!account) return <div className="empty-message">Nenhuma informação encontrada.</div>;

  const photoSrc = account.profile_photo && !imageError ? account.profile_photo : default_profile_image_url;
  const isOwnProfile = user && account.user_id === user.cognitoUser.username;
  const canMessage = user && !isOwnProfile;

  return (
    <div className="profile-public">
      <div className="profile-card">
        <div className="profile-cover" />

        <div className="profile-photo-wrap">
          <img
            src={photoSrc}
            alt={`Foto de ${account.full_name || "perfil"}`}
            className="profile-photo-img"
            onError={() => setImageError(true)}
          />
        </div>

        <div className="profile-identity">
          <h1 className="profile-name">{account.full_name || "—"}</h1>
          <p className="profile-handle">@{account.slug}</p>

          <div className="profile-badge">
            <AccountSpecificMessagePublic account_type={account.account_type} />
          </div>

          {canMessage && (
            <button
              type="button"
              className="profile-dm-btn"
              onClick={() => initiateDirectMessage(account.user_id)}
            >
              <Send size={15} /> Enviar mensagem
            </button>
          )}
        </div>

        <div className="profile-details">
          <div className="profile-detail-item">
            <span className="profile-detail-label">Sobre</span>
            <p className="profile-bio">
              {account.biography || "(usuário sem biografia cadastrada)"}
            </p>
          </div>

          <div className="profile-detail-item">
            <span className="profile-detail-label">Link</span>
            <span className="profile-link-value">
              <LinkIcon size={16} />
              {account.link ? (
                <a href={account.link} target="_blank" rel="noopener noreferrer">
                  {account.link}
                </a>
              ) : (
                <span className="profile-link-empty">(sem link)</span>
              )}
            </span>
          </div>
        </div>
      </div>

      <section className="profile-posts-section">
        <h2 className="profile-posts-title">Publicações recentes</h2>

        {isOwnProfile && <CreatePost onCreate={handleCreatePost} />}

        {postsLoading ? (
          <p className="profile-posts-empty">Carregando publicações...</p>
        ) : posts.length === 0 ? (
          <p className="profile-posts-empty">Este usuário ainda não publicou nada.</p>
        ) : (
          <div className="profile-posts-grid">
            {posts.map((post) => (
              <PostPreviewCard
                key={post.id}
                post={post}
                isOwner={isOwnProfile}
                onEdit={setEditingPost}
                onRequestDelete={setPostPendingDelete}
                actionsDisabled={deletingPost}
              />
            ))}
          </div>
        )}
      </section>

      <EditPostModal
        post={editingPost}
        open={Boolean(editingPost)}
        onClose={() => setEditingPost(null)}
        onUpdated={handlePostUpdated}
      />

      <ConfirmDialog
        open={Boolean(postPendingDelete)}
        title="Excluir publicação"
        message="Tem certeza de que deseja excluir esta publicação? Os comentários também serão removidos."
        confirmLabel="Excluir"
        busy={deletingPost}
        busyLabel="Excluindo..."
        danger
        onConfirm={handleConfirmDeletePost}
        onCancel={() => {
          if (!deletingPost) setPostPendingDelete(null);
        }}
      />
    </div>
  );
}

export default AccountPublic;
